'use client';

import type { Theme, SxProps } from '@mui/material/styles';

import { useState } from 'react';

import Box from '@mui/material/Box';

import { RADIUS } from 'src/theme/ui-tokens';

// ----------------------------------------------------------------------

/**
 * **채널 아이콘** — 인스타·틱톡·유튜브·네이버·X …
 *
 * 자산은 `public/assets/icons/platforms/<채널>.svg` 에 이미 다 있다. 채널 id 가 곧 파일
 * 이름이라 표를 따로 두지 않는다 — 새 채널이 늘어도 파일만 놓으면 붙는다.
 *
 * ⚠️ **색을 입히지 않는다.** `SvgColor` 로 단색 처리하면 인스타 그라데이션·유튜브 빨강이
 * 사라져 채널 식별이 안 된다 — 이 아이콘은 장식이 아니라 「어디에 올라온 글인가」다.
 *
 * ⚠️ 없는 채널이면 **첫 두 글자**를 대신 그린다. 빈 자리를 두면 줄이 어긋난다.
 */
export type PlatformIconProps = {
  /** `/assets/icons/platforms/<id>.svg` 의 파일명이 된다 */
  platformId: string;
  size?: number;
  sx?: SxProps<Theme>;
};

export function PlatformIcon({ platformId, size = 24, sx }: PlatformIconProps) {
  const [failed, setFailed] = useState(false);
  const id = String(platformId ?? '').toLowerCase();

  if (!id || failed) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: RADIUS.sm,
          bgcolor: 'text.disabled',
          color: 'common.white',
          fontSize: size * 0.38,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
      >
        {id.toUpperCase().slice(0, 2)}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={`/assets/icons/platforms/${id}.svg`}
      alt={id}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{ width: size, height: size, flexShrink: 0, objectFit: 'contain', display: 'block', ...sx }}
    />
  );
}
